import { useEffect } from 'react'
import { useNotifications } from '../components/Elements/NotificationSystem'

/**
 * Hook to handle global notification events
 * This allows components without direct access to notification context to trigger notifications
 */
export function useNotificationHandler() {
  const notifications = useNotifications()

  useEffect(() => {
    const handleNotificationEvent = (event: CustomEvent) => {
      const { type, title, message, ...options } = event.detail

      switch (type) {
        case 'success':
          notifications.showSuccess(title, message, options)
          break
        case 'error':
          notifications.showError(title, message, options)
          break
        case 'warning':
          notifications.showWarning(title, message, options)
          break
        case 'info':
          notifications.showInfo(title, message, options)
          break
        default:
          console.warn('Unknown notification type:', type)
      }
    }

    // Listen for custom notification events
    window.addEventListener('jirax-notification', handleNotificationEvent as EventListener)

    return () => {
      window.removeEventListener('jirax-notification', handleNotificationEvent as EventListener)
    }
  }, [notifications])
}

/**
 * Utility functions to trigger notifications from anywhere in the app
 */
export const notificationUtils = {
  success: (title: string, message?: string, options?: any) => {
    const event = new CustomEvent('jirax-notification', {
      detail: { type: 'success', title, message, ...options }
    })
    window.dispatchEvent(event)
  },

  error: (title: string, message?: string, options?: any) => {
    const event = new CustomEvent('jirax-notification', {
      detail: { type: 'error', title, message, ...options }
    })
    window.dispatchEvent(event)
  },

  warning: (title: string, message?: string, options?: any) => {
    const event = new CustomEvent('jirax-notification', {
      detail: { type: 'warning', title, message, ...options }
    })
    window.dispatchEvent(event)
  },

  info: (title: string, message?: string, options?: any) => {
    const event = new CustomEvent('jirax-notification', {
      detail: { type: 'info', title, message, ...options }
    })
    window.dispatchEvent(event)
  }
}